import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import db from '../data/db';
import { GCSE_DECKS } from '../data/gcseDecks';
import { createNewCardState } from '../data/fsrs';
import { useApp } from '../context/AppContext';
import { FiArrowLeft, FiCheck, FiPlus, FiLock } from 'react-icons/fi';

export default function GcseLibrary() {
  const navigate = useNavigate();
  const { hasOnboarded, completeOnboarding } = useApp();
  const [addedKeys, setAddedKeys] = useState(new Set());
  const [addingKey, setAddingKey] = useState(null);

  useEffect(() => {
    loadAdded();
  }, []);

  async function loadAdded() {
    const systemDecks = await db.decks.where('systemDeckKey').notEqual('').toArray();
    setAddedKeys(new Set(systemDecks.map(d => d.systemDeckKey)));
  }

  async function addDeck(definition) {
    setAddingKey(definition.key);
    try {
      const now = new Date().toISOString();
      const deckId = await db.decks.add({
        name: definition.name,
        description: definition.description,
        emoji: definition.emoji,
        cardCount: definition.cards.length,
        isSystem: 1,
        systemDeckKey: definition.key,
        createdAt: now,
        updatedAt: now,
        dirty: 1,
        remoteId: '',
        syncedAt: '',
      });

      const cardEntries = definition.cards.map(card => ({
        deckId,
        front: card.front,
        back: card.back,
        fsrs: createNewCardState(),
        createdAt: now,
        updatedAt: now,
        dirty: 1,
        remoteId: '',
        remoteDeckId: '',
        syncedAt: '',
      }));
      await db.cards.bulkAdd(cardEntries);

      setAddedKeys(prev => new Set(prev).add(definition.key));
      toast.success(`${definition.name} added to your decks`);

      // Landing-page visitors browse the library BEFORE onboarding; their
      // first added deck completes onboarding and drops them into studying —
      // one tap from the web page to a live review session.
      if (!hasOnboarded) {
        await completeOnboarding();
        navigate(`/study/${deckId}`);
        return;
      }
    } catch (err) {
      console.error('Failed to add GCSE deck:', err);
      toast.error('Could not add deck — please try again');
    } finally {
      setAddingKey(null);
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => navigate('/')}
            style={{ marginBottom: 'var(--space-sm)' }}
          >
            <FiArrowLeft /> {hasOnboarded ? 'Back to decks' : 'Back'}
          </button>
          <h1>GCSE Library</h1>
          <p className="page-subtitle">
            Curated decks for every major British GCSE subject. Tap to add — these
            decks stay in your library and can’t be deleted.
          </p>
        </div>
      </div>

      <div className="deck-grid">
        {GCSE_DECKS.map(deck => {
          const isAdded = addedKeys.has(deck.key);
          const isAdding = addingKey === deck.key;

          return (
            <div key={deck.key} className="deck-card" style={{ cursor: 'default' }}>
              <div className="deck-card-header">
                <span className="deck-emoji">{deck.emoji}</span>
                <span
                  title="Library deck (cannot be deleted)"
                  style={{
                    color: 'var(--text-tertiary)',
                    fontSize: '0.75rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <FiLock /> Library
                </span>
              </div>

              <h3 className="deck-name">{deck.name}</h3>
              <p className="deck-description">{deck.description}</p>

              <div className="deck-counts">
                <span className="count-new">{deck.cards.length} cards</span>
              </div>

              {isAdded ? (
                <button className="btn btn-secondary" disabled style={{ marginTop: 'auto' }}>
                  <FiCheck /> Added
                </button>
              ) : (
                <button
                  className="btn btn-primary"
                  onClick={() => addDeck(deck)}
                  disabled={isAdding}
                  style={{ marginTop: 'auto' }}
                >
                  {isAdding ? (
                    <>Adding…</>
                  ) : (
                    <><FiPlus /> Add to my decks</>
                  )}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
